import { chakra } from "@chakra-ui/react";
import { replacePlaceholders } from "../layout/documentLayout";
import { answerAsString, firstAnswer, formatAmericanDate, readField } from "./answerFormat";
import { MutedNote, TitledBlock } from "./printStyles";
import type { QuestionViewProps } from "./types";

// RNPlatformPatientPhysician, medicationProfileItem, vbpItem: complex or computed types. Show the
// label plus whatever the stored answer says as key/value lines; otherwise point to the generated
// PDF.

interface KeyValue {
  key: string;
  value: string;
}

function text(value: unknown): string {
  return answerAsString(value) ?? "";
}

function physicianLines(answer: unknown): KeyValue[] {
  if (typeof answer !== "object" || answer === null || Array.isArray(answer)) {
    return [];
  }
  const phones = readField(answer, "phoneNumbers");
  const faxes = readField(answer, "faxNumbers");
  return [
    { key: "Name", value: text(readField(answer, "name")) },
    { key: "NPI", value: text(readField(answer, "npi")) || "Empty" },
    { key: "Address", value: text(readField(answer, "address")) },
    {
      key: "Phone Numbers",
      value: Array.isArray(phones)
        ? phones
            .map((phone) => [
              text(readField(phone, "number")),
              text(readField(phone, "institution")),
            ])
            .map(([number, institution]) =>
              institution === "" ? number : `${number} (${institution})`
            )
            .join(", ")
        : "",
    },
    { key: "Fax Numbers", value: Array.isArray(faxes) ? faxes.map(text).join(", ") : "" },
  ].filter((line) => line.value !== "");
}

function medicationLines(answer: unknown): KeyValue[] {
  if (!Array.isArray(answer)) {
    return [];
  }
  return (answer as unknown[]).map((medication) => {
    const details = [
      text(readField(medication, "dose")),
      text(readField(medication, "route")),
      text(readField(medication, "frequency")),
    ].filter((part) => part !== "");
    const from = formatAmericanDate(readField(medication, "from"));
    const until = formatAmericanDate(readField(medication, "until"));
    if (from !== "" || until !== "") {
      details.push(`${from || "?"} - ${until || "ongoing"}`);
    }
    return {
      key: text(readField(medication, "medicationName")) || "Medication",
      value: details.join(", "),
    };
  });
}

function fallbackLines(itemType: string, answer: unknown): KeyValue[] {
  switch (itemType) {
    case "RNPlatformPatientPhysician":
      return physicianLines(answer);
    case "medicationProfile":
    case "medicationProfileItem":
      return medicationLines(answer);
    default: {
      const value = answerAsString(answer);
      return value === null || value === "" ? [] : [{ key: "Answer", value }];
    }
  }
}

export function FallbackQuestionView({ block, placeholders }: QuestionViewProps) {
  const lines = fallbackLines(block.question.itemType, firstAnswer(block.answers));

  return (
    <TitledBlock
      gap="6px"
      isInline={false}
      title={replacePlaceholders(block.question.label, placeholders)}
    >
      {lines.length === 0 ? (
        <MutedNote>Shown in the generated PDF</MutedNote>
      ) : (
        lines.map((line, index) => (
          <chakra.div key={index} display="flex" gap="16px">
            <chakra.strong flexShrink={0} fontWeight={600}>
              {line.key}:
            </chakra.strong>
            <span>{line.value}</span>
          </chakra.div>
        ))
      )}
    </TitledBlock>
  );
}
